import { ButtonGroup, Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { DogChipApi, DogChipGridDto, DogChipSearch } from "../../../../../../api";
import { CustomDataTable, DeleteButton, EditButton, ExportButton } from "../../../../../../components";
import { useGlobalData } from "../../../../../../contexts";
import { useApiService, useDeleteApi, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { DogChipPanel } from "./DogChipPanel";

export interface IDogChipNumersListProps {
  dogId: number;
  editMode: boolean;
}
const Container = styled.div`
  flex: 1;
  height: 100%;
  & > .${Classes.PANEL_STACK} {
    height: 100%;
  }
`;
export const DogChipNumersList: React.FunctionComponent<IDogChipNumersListProps> = ({ dogId, editMode }) => {
  const { t } = useTl();
  const api = useApiService(DogChipApi);
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { createdOn: "DESC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogChipSearch) => {
      setsobjState(DogChipSearch.fromJS({ ...sObj, dogId: dogId }));
      return api.baseSearch(DogChipSearch.fromJS({ ...sObj, dogId: dogId }));
    },
    [api, dogId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const [SelectedChipId, setSelectedChipId] = React.useState(0);

  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.delete(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );
  const addItemFunction = React.useCallback((chipId: number) => {
    setDialogOpened(true);
    setSelectedChipId(chipId);
  }, []);
  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: DogChipGridDto) => (
          <ButtonContainer>
            <EditButton minimal={true} onClick={() => addItemFunction(row.dogChipId)} />
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.dogChipId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.ChipNumber),
        fieldName: "chipNumber",
      },
      {
        header: () => t(ETLCodes.ChipDate),
        fieldName: "createdOn",
      },
    ],
    [addItemFunction, deleteItem, editMode, t]
  );

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.DogChips)}
        rightElement={
          <ButtonGroup>
            {editMode && (
              <EditButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction(null);
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
            <ExportButton sObj={sobjState} exportFunction={(sObj) => api.exportXlsFile(sObj)} />
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.DogChipCreation)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1000, background: Colors.WHITE }}
      >
        <Container className={Classes.DIALOG_BODY}>
          <DogChipPanel
            dogId={dogId}
            dogChipId={SelectedChipId}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </Container>
      </Dialog>
    </>
  );
};
